import moment from 'moment';
import humanizeUtil, { humanize } from '@utils/humanizeUtil';
import { TOOLTIP_LABEL_FORMAT } from '@components/common/Charts/utils';
import {
  DAY,
  GRANULARITY,
  HOUR,
  SIX_HOURS,
  THIRTY_MIN,
  TWELVE_HOURS,
  TWO_HOURS,
} from '@src/utils/granularity';

const ImpactedDevicesKey = 'Impacted Devices';
const NonImpactedDevicesKey = 'Non-Impacted Devices';
const ImpactedViewersKey = 'Impacted Viewers';
const NonImpactedViewersKey = 'Non-Impacted Viewers';

const Messages = {
  [ImpactedDevicesKey]: 'Devices with impacted streams',
  [NonImpactedDevicesKey]: 'Devices with no impacted streams',
  [ImpactedViewersKey]: 'Viewers with impacted streams',
  [NonImpactedViewersKey]: 'Viewers with no impacted streams',
};

const tooltipFormatter = (series) => {
  try {
    const { value } = series[0].data;
    const timestamp = moment(value[0]).format(TOOLTIP_LABEL_FORMAT);

    const rows = series.map((item) => {
      const { humanizedNumber, humanizedUnit } = humanizeUtil(item.data.value[1], '');
      const displayName = item.seriesName;
      return `<br/>${displayName} - <b>${`${humanizedNumber} ${humanizedUnit}`.trimEnd()}</b>`;
    });
    return `${timestamp} ${rows.reverse()}`;
  } catch (err) {
    return '-';
  }
};

const buildChartOptions = ({ nonImpactedKey, impactedKey, goodBarData, badBarData }) => {
  const option = {
    grid: {
      top: 20,
      left: 40,
      right: 20,
      bottom: 5,
    },
    legend: {
      right: 20,
      itemWidth: 15,
      itemHeight: 12,
      itemStyle: {
        borderColor: '#aaa',
      },
      textStyle: {
        fontSize: 12,
      },
      tooltip: {
        show: true,
        formatter: (item) => Messages[item.name],
      },
    },
    xAxis: {
      type: 'time',
      show: false,
    },
    yAxis: {
      type: 'value',
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      axisLabel: {
        formatter: (value) => humanize(value, ''),
        color: '#333',
        fontSize: 12,
        showMinLabel: true,
      },
      splitNumber: 2,
      splitLine: {
        lineStyle: {
          color: '#e5e5e5a3',
        },
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      formatter: tooltipFormatter,
    },
    series: [
      {
        name: nonImpactedKey,
        type: 'bar',
        stack: 'devices',
        data: goodBarData,
        itemStyle: {
          color: '#D8EFD3',
          borderColor: '#72BF78',
        },
        cursor: 'default',
      },
      {
        name: impactedKey,
        type: 'bar',
        stack: 'devices',
        data: badBarData,
        itemStyle: {
          color: '#FFACAC',
          borderColor: '#EE4E4E',
        },
        cursor: 'default',
      },
    ],
  };
  return option;
};

export const formatViewersData = (records, selectedBarIndex = null) => {
  const goodBarData: any = [];
  const badBarData: any = [];

  const defItemStyle = { opacity: 1 };

  records.forEach(({ timestamp, results }) => {
    goodBarData.push({
      value: [timestamp, results.GoodUniqueViewers],
      itemStyle: defItemStyle,
    });
    badBarData.push({
      value: [timestamp, results.BadUniqueViewers],
      itemStyle: defItemStyle,
    });
  });

  // highlight a section
  if (records.length && selectedBarIndex !== null) {
    const highlightIndex = selectedBarIndex === -1 ? goodBarData.length - 1 : selectedBarIndex;
    if (goodBarData.length >= highlightIndex) {
      goodBarData[highlightIndex].itemStyle = {
        color: '#ADE0C1',
        opacity: 1,
      };
      badBarData[highlightIndex].itemStyle = {
        opacity: 1,
        color: '#EFABAB',
      };

      // update all referenced bars
      defItemStyle.opacity = 0.4;
    }
  }

  const options = buildChartOptions({
    impactedKey: ImpactedViewersKey,
    nonImpactedKey: NonImpactedViewersKey,
    goodBarData,
    badBarData,
  });

  return options;
};

/*
granularity x interval range
1m <= 30m
5m <= 2h
15m <= 6h
30m <= 12h
1h <= 24h
*/
export const getBarGranularity = ({ from, to }) => {
  const diffInMins = moment(to).diff(moment(from), 'minutes');
  let validGranularity;
  switch (true) {
    case diffInMins <= THIRTY_MIN:
      validGranularity = GRANULARITY.PT1M;
      break;
    case diffInMins <= TWO_HOURS:
      validGranularity = GRANULARITY.PT5M;
      break;
    case diffInMins <= SIX_HOURS:
      validGranularity = GRANULARITY.PT15M;
      break;
    case diffInMins <= TWELVE_HOURS:
      validGranularity = GRANULARITY.PT30M;
      break;
    case diffInMins <= DAY: // <= 1 day
      validGranularity = GRANULARITY.PT1H;
      break;
    default:
      validGranularity = GRANULARITY.PT1H;
  }
  return validGranularity;
};
